import { useEffect, useState } from "react";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface Conera {
  id: string;
  name: string;
  lastVersion: number;
  lastCheck: number | null;
  isUpdated: boolean;
  isStale: boolean;
}

interface Stats {
  total: number;
  updated: number;
  pending: number;
  noData: number;
  stale: number;
  latestVersion: number;
  latestDescription: string;
  lastUpload: number | null;
  coneras: Conera[];
}

export default function PublicDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/public/stats")
      .then((r) => { if (!r.ok) throw new Error("Error"); return r.json(); })
      .then(setStats)
      .catch(() => setError("No se pudo cargar el dashboard"))
      .finally(() => setLoading(false));
  }, []);

  function fmtDate(ts: number | null) {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString("es-MX", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  const pieData = stats ? [
    { name: "Actualizadas", value: stats.updated, color: "#34d399" },
    { name: "Pendientes", value: stats.pending, color: "#fbbf24" },
    { name: "Sin datos", value: stats.noData, color: "#71717a" },
  ].filter((d) => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">DBF Monitor</h1>
            <p className="text-xs text-zinc-500">Estado de las coneras</p>
          </div>
          <div className="text-right text-xs text-zinc-500">
            {stats && <p>V{stats.latestVersion} — {stats.latestDescription}</p>}
            {stats && stats.lastUpload && <p>Actualizado: {fmtDate(stats.lastUpload)}</p>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4">
        {loading ? (
          <div className="text-zinc-500 text-center py-12">Cargando...</div>
        ) : error ? (
          <div className="text-red-400 text-center py-12">{error}</div>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <p className="text-xs text-zinc-500 uppercase">Total</p>
                <p className="text-2xl font-bold text-zinc-100">{stats.total}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                <p className="text-xs text-emerald-500 uppercase">Actualizadas</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.updated}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <p className="text-xs text-amber-500 uppercase">Pendientes</p>
                <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
              </div>
              <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
                <p className="text-xs text-zinc-500 uppercase">Sin datos</p>
                <p className="text-2xl font-bold text-zinc-400">{stats.noData}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h2 className="text-sm font-semibold text-zinc-100 mb-3">Estado</h2>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none">
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: "8px", color: "#fff", fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((d) => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                      <span className="text-xs text-zinc-400">{d.name} ({d.value})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h2 className="text-sm font-semibold text-zinc-100 mb-3">Coneras sin actualizar en 24h</h2>
                {stats.stale === 0 ? (
                  <div className="flex items-center justify-center h-[180px]">
                    <p className="text-emerald-400 text-sm">Todas al dia</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[180px] overflow-y-auto">
                    {stats.coneras.filter((c) => c.isStale).map((c) => (
                      <div key={c.id} className="flex justify-between text-sm">
                        <span className="text-zinc-300">{c.id}</span>
                        <span className="text-zinc-500 text-xs">{fmtDate(c.lastCheck)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h2 className="text-sm font-semibold text-zinc-100">Todas las coneras ({stats.total})</h2>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {stats.coneras.map((c) => (
                  <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-medium text-zinc-100 text-sm">{c.id}</span>
                      <span className="text-zinc-500 text-xs ml-2 hidden sm:inline">{c.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-zinc-500 text-xs hidden sm:inline">{fmtDate(c.lastCheck)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        !c.lastCheck ? "bg-zinc-800 text-zinc-500" :
                        c.isUpdated ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"
                      }`}>
                        {!c.lastCheck ? "Sin datos" : c.isUpdated ? "OK" : "Pendiente"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-zinc-600 mt-4 text-center">Actualiza cada 30 segundos</p>
          </>
        ) : null}
      </main>
    </div>
  );
}
